import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Collapse,
  IconButton,
  Divider
} from '@mui/material';
import {
  Storefront as StoreIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import moment from 'moment';

const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  
  const [entero, decimal] = numValue.toFixed(2).split('.');
  const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${enteroFormateado},${decimal}`;
};

// Funciones auxiliares (importadas o duplicadas del componente padre)
const getMetodoPagoLabel = (paymentMethod) => {
  const metodos = {
    'visa': 'Visa',
    'master': 'Mastercard',
    'amex': 'American Express',
    'cabal': 'Cabal',
    'naranja': 'Naranja',
    'argencard': 'Argencard',
    'diners': 'Diners',
    'debvisa': 'Visa Débito',
    'debmaster': 'Mastercard Débito',
    'debcabal': 'Cabal Débito',
    'maestro': 'Maestro',
    'visa_prepago': 'Visa Prepago',
    'master_prepago': 'Mastercard Prepago',
    'pagofacil': 'Pago Fácil',
    'rapipago': 'Rapipago',
    'account_money': 'Saldo Mercado Pago',
    'interop_transfer': 'Transferencia Bancaria Interoperable',
    'debin': 'DEBIN',
    'transferencia': 'Transferencia Bancaria',
    'pix': 'Pix',
    'paypal': 'PayPal'
  };
  
  if (metodos[paymentMethod]) return metodos[paymentMethod];
  
  const metodoEncontrado = Object.keys(metodos).find(key => 
    paymentMethod?.toLowerCase().includes(key.toLowerCase()) || 
    key.toLowerCase().includes(paymentMethod?.toLowerCase())
  );
  
  return metodoEncontrado ? metodos[metodoEncontrado] : paymentMethod || 'Desconocido';
};

const getMetodoPagoIcon = (paymentMethod) => {
  if (paymentMethod === 'account_money') {
    return <AccountBalanceIcon sx={{ fontSize: 14, color: '#1976d2' }} />;
  }
  if (paymentMethod === 'interop_transfer') {
    return <AccountBalanceIcon sx={{ fontSize: 14, color: '#7b1fa2' }} />;
  }
  const creditCards = ['visa', 'master', 'amex', 'cabal', 'naranja', 'argencard', 'diners'];
  if (creditCards.includes(paymentMethod)) {
    return <CreditCardIcon sx={{ fontSize: 14, color: '#4caf50' }} />;
  }
  const debitCards = ['debvisa', 'debmaster', 'debcabal', 'maestro'];
  if (debitCards.includes(paymentMethod)) {
    return <CreditCardIcon sx={{ fontSize: 14, color: '#ff9800' }} />;
  }
  const tickets = ['pagofacil', 'rapipago'];
  if (tickets.includes(paymentMethod)) {
    return <PaymentIcon sx={{ fontSize: 14, color: '#9c27b0' }} />;
  }
  return <CreditCardIcon sx={{ fontSize: 14, color: '#757575' }} />;
};

const getEstadoAcreditacion = (moneyReleaseStatus) => {
  const status = moneyReleaseStatus?.toLowerCase?.() || '';
  
  if (status === 'released') {
    return {
      label: 'DINERO ACREDITADO',
      color: 'success',
      icon: <PriceCheckIcon sx={{ fontSize: 12 }} />
    };
  } else if (status === 'pending') {
    return {
      label: 'DINERO PENDIENTE',
      color: 'warning',
      icon: <MoneyOffIcon sx={{ fontSize: 12 }} />
    };
  } else {
    return {
      label: 'DESCONOCIDO',
      color: 'default',
      icon: <InfoIcon sx={{ fontSize: 12 }} />
    };
  }
};

const getEstadoChip = (tipo, coincide) => {
  if (tipo === 'coincidencia') {
    if (coincide) {
      return {
        label: 'Coincide',
        color: 'success',
        icon: <CheckCircleIcon sx={{ fontSize: 12 }} />
      };
    } else {
      return {
        label: 'Difiere',
        color: 'warning',
        icon: <WarningIcon sx={{ fontSize: 12 }} />
      };
    }
  } else {
    return {
      label: 'Solo MP',
      color: 'info',
      icon: <InfoIcon sx={{ fontSize: 12 }} />
    };
  }
};

const PagosPorSucursal = ({ pagos, fecha }) => {
  const [expandedSucursales, setExpandedSucursales] = useState({});

  // Agrupar pagos por sucursal
  const pagosAgrupados = useMemo(() => {
    const agrupado = {};
    
    pagos.forEach(pago => {
      const sucursal = pago.dbData?.sucursal || 'Sin Sucursal';
      if (!agrupado[sucursal]) {
        agrupado[sucursal] = {
          sucursal: sucursal,
          pagos: [],
          total: 0,
          cantidad: 0,
          pendientes: 0,
          acreditados: 0
        };
      }
      agrupado[sucursal].pagos.push(pago);
      agrupado[sucursal].total += pago.transaction_amount || 0;
      agrupado[sucursal].cantidad += 1;
      
      const status = pago.money_release_status?.toLowerCase?.() || '';
      if (status === 'pending') {
        agrupado[sucursal].pendientes += 1;
      } else if (status === 'released') {
        agrupado[sucursal].acreditados += 1;
      }
    });

    // Ordenar sucursales por cantidad de pagos (mayor a menor)
    const sorted = Object.keys(agrupado).sort((a, b) => 
      agrupado[b].cantidad - agrupado[a].cantidad
    );
    
    const resultado = {};
    sorted.forEach(key => {
      resultado[key] = agrupado[key];
    });
    
    return resultado;
  }, [pagos]);

  const toggleSucursal = (sucursal) => {
    setExpandedSucursales(prev => ({
      ...prev,
      [sucursal]: !prev[sucursal]
    }));
  };

  const sucursales = Object.keys(pagosAgrupados);

  if (sucursales.length === 0) {
    return (
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="caption" color="textSecondary">
          No hay pagos para mostrar
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#424242' }}>
        <StoreIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
        Distribución por Sucursal
      </Typography>
      
      <Divider sx={{ mb: 1.5 }} />

      <Grid container spacing={1.5}>
        {sucursales.map((sucursalKey) => {
          const sucursalData = pagosAgrupados[sucursalKey];
          const isExpanded = expandedSucursales[sucursalKey] || false;
          const tienePendientes = sucursalData.pendientes > 0;

          return (
            <Grid item xs={12} key={sucursalKey}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${isExpanded ? '#1976d2' : '#e8e8e8'}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#1976d2'
                  }
                }}
              >
                {/* Cabecera de sucursal */}
                <Box
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    bgcolor: isExpanded ? '#f5f9ff' : '#fafafa',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: isExpanded ? '#eef4fc' : '#f0f0f0'
                    }
                  }}
                  onClick={() => toggleSucursal(sucursalKey)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <StoreIcon sx={{ color: tienePendientes ? '#ed6c02' : '#1976d2', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {sucursalKey}
                    </Typography>
                    <Chip
                      label={`${sucursalData.cantidad} pago${sucursalData.cantidad !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ fontSize: '0.8rem', height: 20 }}
                    />
                    {sucursalData.pendientes > 0 && (
                      <Chip
                        label={`${sucursalData.pendientes} pendiente${sucursalData.pendientes !== 1 ? 's' : ''}`}
                        size="small"
                        color="warning"
                        sx={{ fontSize: '0.8rem', height: 20 }}
                      />
                    )}
                    {sucursalData.acreditados > 0 && (
                      <Chip
                        label={`${sucursalData.acreditados} acreditado${sucursalData.acreditados !== 1 ? 's' : ''}`}
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.8rem', height: 20 }}
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      ${formatNumber(sucursalData.total)}
                    </Typography>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Detalle de pagos de la sucursal */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                    <Grid container spacing={1}>
                      {sucursalData.pagos.map((pago, index) => {
                        const estado = getEstadoChip(pago.tipo, pago.coincide);
                        const estadoAcreditacion = getEstadoAcreditacion(pago.money_release_status);
                        
                        return (
                          <Grid item xs={12} key={index}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1,
                                bgcolor: 'white',
                                border: '1px solid #e8e8e8',
                                borderRadius: 1
                              }}
                            >
                              <Grid container spacing={0.5} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getMetodoPagoIcon(pago.payment_method)}
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {pago.external_reference || pago.id || 'Sin referencia'}
                                    </Typography>
                                  </Box>
                                </Grid>

                                <Grid item xs={6} sm={2}>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.8rem' }}>
                                    Método
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                    {getMetodoPagoLabel(pago.payment_method)}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6} sm={2}>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.8rem' }}>
                                    Monto
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', fontSize: '0.9rem' }}>
                                    ${formatNumber(pago.transaction_amount)}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                  <Chip
                                    label={estado.label}
                                    color={estado.color}
                                    size="small"
                                    icon={estado.icon}
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                  <Chip
                                    label={estadoAcreditacion.label}
                                    color={estadoAcreditacion.color}
                                    size="small"
                                    icon={estadoAcreditacion.icon}
                                    sx={{ fontSize: '0.7rem', height: 20, ml: 0.5 }}
                                  />
                                  {pago.dbData?.pedido && (
                                    <Chip
                                      label={`Pedido: ${pago.dbData.pedido}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20, ml: 0.5 }}
                                    />
                                  )}
                                </Grid>

                                {pago.dbData && (
                                  <Grid item xs={12}>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      gap: 1.5, 
                                      flexWrap: 'wrap',
                                      mt: 0.5,
                                      pt: 0.5,
                                      borderTop: '1px dashed #e8e8e8'
                                    }}>
                                      {pago.dbData.cliente && (
                                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                          CLIENTE: {pago.dbData.cliente}
                                        </Typography>
                                      )}
                                      {pago.dbData.chofer && (
                                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                          CHOFER: {pago.dbData.chofer}
                                        </Typography>
                                      )}
                                      {pago.date_approved && (
                                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                          FECHA APROBADO: {moment(pago.date_approved).format('DD/MM HH:mm')}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {/* Resumen de la sucursal */}
                    <Box sx={{ 
                      mt: 1.5, 
                      pt: 1, 
                      borderTop: '2px solid #e8e8e8',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.9rem' }}>
                            Pagos
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center' }}>
                            {sucursalData.cantidad}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.9rem' }}>
                            Pendientes
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center', color: '#ed6c02' }}>
                            {sucursalData.pendientes}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.9rem' }}>
                            Total
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                            ${formatNumber(sucursalData.total)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PagosPorSucursal;